/**
 * @fileoverview Org portal — admin self-management (Spec 11 WP4). Lists the
 * org's admins with activate/deactivate, an add-admin form (the new admin sets
 * their own password via an emailed link). Your own password, deleting your
 * account and Appearance moved to Settings › You (OrgAccountPage,
 * OrgAppearancePage) in iOS review C10, so this page is only the people.
 * An admin who has not used their link yet is marked Invited and can be sent
 * a fresh one; the server retires the old link and refuses a second resend
 * within a minute.
 */

import { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { OrgAuthContext } from "@/App";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Card, SectionHeader, Button, ListRow, Avatar, Tag } from "@/components/studio/primitives";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const field =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[11px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";

export default function OrgAdminsPage() {
  const { admin: me } = useContext(OrgAuthContext) || {};
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [adding, setAdding] = useState(false);
  const [resending, setResending] = useState(null); // admin_id while its resend is in flight

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await orgApi.get("/org/admins");
      setAdmins(resp.data.admins || []);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't load admins"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      toast.error("Enter a valid email");
      return;
    }
    setAdding(true);
    try {
      await orgApi.post("/org/admins", { email: email.trim(), first_name: firstName.trim() });
      setEmail("");
      setFirstName("");
      toast.success("Admin added — they've been emailed a link to set their password");
      load();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't add admin"));
    } finally {
      setAdding(false);
    }
  };

  const setActive = async (target, active) => {
    const verb = active ? "Reactivate" : "Deactivate";
    if (!active && !window.confirm(`Deactivate ${target.email}? They are signed out immediately.`)) return;
    try {
      await orgApi.put(`/org/admins/${target.admin_id}/active`, { active });
      toast.success(`${verb}d ${target.email}`);
      load();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, `Couldn't ${verb.toLowerCase()} admin`));
    }
  };

  const resendInvite = async (target) => {
    setResending(target.admin_id);
    try {
      const resp = await orgApi.post(`/org/admins/${target.admin_id}/resend-invite`);
      if (resp.data?.email_sent === false) {
        toast.error(`Couldn't email ${target.email}. Try again in a minute.`);
      } else {
        toast.success(`New invitation sent to ${target.email}`);
      }
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't resend the invitation"));
      // A 409 means their state moved on (they set a password, or were
      // deactivated) — show the list as it is now.
      if (err?.response?.status === 409) load();
    } finally {
      setResending(null);
    }
  };

  return (
    <div>
      <h1 className="mb-6 font-serif text-2xl font-semibold text-[var(--text-primary)]">Admins</h1>

      {/* Add admin */}
      <Card className="mb-6">
        <SectionHeader>Add an admin</SectionHeader>
        <form onSubmit={add} className="flex flex-col gap-3 sm:flex-row">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@email.com"
            data-testid="org-admin-email"
            className={field + " flex-1 rounded-full"}
          />
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name (optional)"
            className={field + " rounded-full sm:max-w-[200px]"}
          />
          <Button type="submit" disabled={adding} icon="mail" data-testid="org-admin-add">
            {adding ? "Adding…" : "Add admin"}
          </Button>
        </form>
        <p className="mt-2 font-sans text-[12px] text-[var(--text-tertiary)]">
          They'll receive an email with a link to set their own password. It works for 30 days.
        </p>
      </Card>

      {/* Admin list */}
      <Card padding={0} className="mb-6">
        {loading ? (
          <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</div>
        ) : (
          admins.map((a, i) => {
            const isMe = a.admin_id === me?.admin_id;
            const name = `${a.first_name || ""} ${a.last_name || ""}`.trim() || a.email;
            return (
              <ListRow
                key={a.admin_id}
                borderBottom={i < admins.length - 1}
                leading={<Avatar name={name} />}
                title={name}
                subtitle={a.email}
                trailing={
                  <div className="flex items-center gap-2">
                    {isMe && <Tag accent>You</Tag>}
                    {!a.active && <Tag>Inactive</Tag>}
                    {a.active && a.pending && <Tag>Invited</Tag>}
                    {a.active && a.pending && (
                      <button
                        type="button"
                        onClick={() => resendInvite(a)}
                        disabled={resending === a.admin_id}
                        data-testid={`org-admin-resend-${a.admin_id}`}
                        className="font-sans text-[13px] font-medium text-[var(--text-secondary)] hover:underline disabled:opacity-50"
                      >
                        {resending === a.admin_id ? "Sending…" : "Resend invite"}
                      </button>
                    )}
                    {!isMe && (
                      <button
                        type="button"
                        onClick={() => setActive(a, !a.active)}
                        data-testid={`org-admin-toggle-${a.admin_id}`}
                        className={[
                          "font-sans text-[13px] font-medium hover:underline",
                          a.active ? "text-[var(--state-danger)]" : "text-[var(--text-secondary)]",
                        ].join(" ")}
                      >
                        {a.active ? "Deactivate" : "Reactivate"}
                      </button>
                    )}
                  </div>
                }
              />
            );
          })
        )}
      </Card>
    </div>
  );
}
